import { isValidEmail } from '@/shared/lib/email';
import type { ClaimProfile, FilingProfilePayload } from '../types';

export const EMPTY_PROFILE: ClaimProfile = {
  first_name: '',
  middle_name: '',
  last_name: '',
  street_address: '',
  country_code: 'US',
  city: '',
  state: '',
  postal_code: '',
  email: '',
};

export const PERSON_NAME_MAX_LENGTH = 50;
export const CITY_MAX_LENGTH = 80;
export const US_ZIP_MAX_LENGTH = 10;

const REQUIRED: Array<keyof ClaimProfile> = [
  'first_name',
  'last_name',
  'street_address',
  'country_code',
  'city',
  'state',
  'postal_code',
  'email',
];

export type ProfileFieldId =
  | 'first_name'
  | 'middle_name'
  | 'last_name'
  | 'street_address'
  | 'city'
  | 'state'
  | 'postal_code'
  | 'email';

export const PROFILE_FIELD_ORDER: ProfileFieldId[] = [
  'first_name',
  'middle_name',
  'last_name',
  'street_address',
  'city',
  'state',
  'postal_code',
  'email',
];

const FIELD_REQUIRED_MESSAGE: Record<Exclude<ProfileFieldId, 'middle_name'>, string> = {
  first_name: 'First name is required',
  last_name: 'Last name is required',
  street_address: 'Street address is required',
  city: 'City is required',
  state: 'State is required',
  postal_code: 'ZIP / Postal Code is required',
  email: 'Email Address is required',
};

/** Unicode letters with a single space, hyphen, or apostrophe between parts. */
const PERSON_NAME_RE = /^[\p{L}]+(?:[ '\u2019-][\p{L}]+)*$/u;

/** City names: letters with spaces, hyphens, apostrophes, or periods between parts. */
const CITY_RE = /^(?=.*\p{L})[\p{L}]+(?:[ .'\u2019-]+[\p{L}]+)*$/u;

/** US ZIP or ZIP+4. */
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

function hasControlChars(value: string): boolean {
  return /\p{C}/u.test(value);
}

function personNameError(value: string, label: string): string | null {
  if (value.length > PERSON_NAME_MAX_LENGTH) {
    return `${label} must be ${PERSON_NAME_MAX_LENGTH} characters or fewer`;
  }
  if (hasControlChars(value) || !PERSON_NAME_RE.test(value)) {
    return `Enter a valid ${label.toLowerCase()}`;
  }
  return null;
}

function cityError(value: string): string | null {
  if (value.length > CITY_MAX_LENGTH) {
    return `City must be ${CITY_MAX_LENGTH} characters or fewer`;
  }
  if (hasControlChars(value) || !CITY_RE.test(value)) {
    return 'Enter a valid city';
  }
  return null;
}

function usZipError(value: string): string | null {
  if (!US_ZIP_RE.test(value)) return 'Enter a valid ZIP code';
  return null;
}

export function profileFieldError(profile: ClaimProfile, field: ProfileFieldId): string | null {
  const value = profile[field].trim();

  if (field === 'middle_name') {
    return value ? personNameError(value, 'Middle name') : null;
  }

  if (!value) return FIELD_REQUIRED_MESSAGE[field];

  switch (field) {
    case 'first_name':
      return personNameError(value, 'First name');
    case 'last_name':
      return personNameError(value, 'Last name');
    case 'city':
      return cityError(value);
    case 'postal_code':
      return usZipError(value);
    case 'email':
      return isValidEmail(value) ? null : 'Enter a valid email address';
    default:
      return null;
  }
}

export function getProfileErrors(profile: ClaimProfile): Partial<Record<ProfileFieldId, string>> {
  const errors: Partial<Record<ProfileFieldId, string>> = {};
  for (const field of PROFILE_FIELD_ORDER) {
    const message = profileFieldError(profile, field);
    if (message) errors[field] = message;
  }
  return errors;
}

export function isProfileComplete(profile: ClaimProfile): boolean {
  for (const key of REQUIRED) {
    if (!profile[key].trim()) return false;
  }
  return Object.keys(getProfileErrors(profile)).length === 0;
}

export function toFilingProfile(profile: ClaimProfile): FilingProfilePayload {
  return {
    first_name: profile.first_name.trim(),
    middle_name: profile.middle_name.trim(),
    last_name: profile.last_name.trim(),
    street_address: profile.street_address.trim(),
    country_code: profile.country_code.trim() || 'US',
    city: profile.city.trim(),
    state: profile.state.trim(),
    postal_code: profile.postal_code.trim(),
    email: profile.email.trim(),
  };
}

export function formatUsDate(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
}
